import React, { useState, useEffect, useRef } from 'react';
import '../styles/Admin.css';
import { toast } from 'react-toastify';

const Admin = () => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ name: '', image: null });
  const [cityForm, setCityForm] = useState({ state: '', city: '' });
  const [vehicleForm, setVehicleForm] = useState({
    name: '',
    type: '',
    city: '',
    prices: { hourly: '', daily: '0', weekly: '', monthly: '' },
    description: '',
    image: null,
    imageUrl: '',
    status: 'Available',
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCity, setEditingCity] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [activeTab, setActiveTab] = useState('categories');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [blockDatesVehicle, setBlockDatesVehicle] = useState(null); // For block dates modal
  const [blockDatesForm, setBlockDatesForm] = useState({
    dateFrom: '',
    timeFrom: '',
    dateTo: '',
    timeTo: '',
  });
  const fileInputRef = useRef(null);

  // Existing useEffect and fetch functions remain unchanged
  useEffect(() => {
    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.role !== 'admin') {
            setError('Access denied. Admin only.');
            setUser(null);
          } else {
            setUser(data);
            await Promise.all([fetchCategories(), fetchCities(), fetchVehicles(), fetchOrders()]);
          }
        } else {
          setError('Failed to fetch user profile');
          setUser(null);
        }
      } catch (error) {
        setError('Network error. Please check your connection.');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        } else {
          setError('Failed to fetch categories');
          setCategories([]);
        }
      } catch (error) {
        setError('Failed to fetch categories: Network error');
        setCategories([]);
      }
    };

    const fetchCities = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cities`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setCities(data);
        } else {
          setError('Failed to fetch cities');
          setCities([]);
        }
      } catch (error) {
        setError('Failed to fetch cities: Network error');
        setCities([]);
      }
    };

    const fetchVehicles = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vehicles`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setVehicles(data);
        } else {
          setError('Failed to fetch vehicles');
        }
      } catch (error) {
        setError('Failed to fetch vehicles: Network error');
      }
    };

    const fetchOrders = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        } else {
          setError('Failed to fetch orders');
          setOrders([]);
        }
      } catch (error) {
        setError('Failed to fetch orders: Network error');
        setOrders([]);
      }
    };

    fetchUser();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('admin-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.admin-fade-in').forEach((el) => observer.observe(el));
    document.querySelectorAll('.admin-slide-up').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Existing handleTabChange, handleCategorySubmit, handleCitySubmit, handleVehicleSubmit,
  // handleCategoryDelete, handleCityDelete, handleVehicleDelete, handleOrderCancel,
  // handleOrderClick, closeModal, startEditingCategory, startEditingCity,
  // startEditingVehicle, cancelVehicleEdit functions remain unchanged
  const handleTabChange = async (tab) => {
    setIsLoading(true);
    setActiveTab(tab);
    try {
      if (tab === 'categories') {
        await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, { credentials: 'include' })
          .then((res) => res.json())
          .then(setCategories);
      } else if (tab === 'cities') {
        await fetch(`${process.env.REACT_APP_API_URL}/api/cities`, { credentials: 'include' })
          .then((res) => res.json())
          .then(setCities);
      } else if (tab === 'vehicles') {
        await fetch(`${process.env.REACT_APP_API_URL}/api/vehicles`, { credentials: 'include' })
          .then((res) => res.json())
          .then(setVehicles);
      } else if (tab === 'orders') {
        await fetch(`${process.env.REACT_APP_API_URL}/api/orders`, { credentials: 'include' })
          .then((res) => res.json())
          .then(setOrders);
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    const formData = new FormData();
    formData.append('name', categoryForm.name);
    if (categoryForm.image) formData.append('image', categoryForm.image);

    try {
      const url = editingCategory
        ? `${process.env.REACT_APP_API_URL}/api/categories/${editingCategory._id}`
        : `${process.env.REACT_APP_API_URL}/api/categories`;
      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        body: formData,
        credentials: 'include',
      });

      if (response.ok) {
        setSuccess(`Category ${editingCategory ? 'updated' : 'created'} successfully`);
        setCategoryForm({ name: '', image: null });
        setEditingCategory(null);
        const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
          credentials: 'include',
        });
        if (refreshResponse.ok) {
          setCategories(await refreshResponse.json());
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save category');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCitySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const url = editingCity
        ? `${process.env.REACT_APP_API_URL}/api/cities/${editingCity._id}`
        : `${process.env.REACT_APP_API_URL}/api/cities`;
      const method = editingCity ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cityForm),
        credentials: 'include',
      });

      if (response.ok) {
        setSuccess(`City ${editingCity ? 'updated' : 'created'} successfully`);
        setCityForm({ state: '', city: '' });
        setEditingCity(null);
        const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/cities`, {
          credentials: 'include',
        });
        if (refreshResponse.ok) {
          setCities(await refreshResponse.json());
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save city');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    const formData = new FormData();
    formData.append('name', vehicleForm.name);
    formData.append('type', vehicleForm.type);
    formData.append('city', vehicleForm.city);
    formData.append('prices', JSON.stringify(vehicleForm.prices));
    formData.append('description', vehicleForm.description);
    formData.append('status', vehicleForm.status);
    if (vehicleForm.image) formData.append('image', vehicleForm.image);

    try {
      const url = editingVehicle
        ? `${process.env.REACT_APP_API_URL}/api/vehicles/${editingVehicle._id}`
        : `${process.env.REACT_APP_API_URL}/api/vehicles`;
      const method = editingVehicle ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        body: formData,
        credentials: 'include',
      });

      if (response.ok) {
        setSuccess(`Bike ${editingVehicle ? 'updated' : 'created'} successfully`);
        const resetForm = {
          name: '',
          type: '',
          city: '',
          prices: { hourly: '', daily: '0', weekly: '', monthly: '' },
          description: '',
          image: null,
          imageUrl: '',
          status: 'Available',
        };
        setVehicleForm(resetForm);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setEditingVehicle(null);
        console.log('Form reset to:', resetForm);
        const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/vehicles`, {
          credentials: 'include',
        });
        if (refreshResponse.ok) {
          setVehicles(await refreshResponse.json());
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save bike');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        setSuccess('Category deleted successfully');
        setCategories(categories.filter((cat) => cat._id !== id));
      } else if (response.status === 404) {
        setError('Category not found. It may have been deleted already.');
        setCategories(categories.filter((cat) => cat._id !== id));
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete category');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCityDelete = async (id) => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cities/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        setSuccess('City deleted successfully');
        setCities(cities.filter((city) => city._id !== id));
      } else if (response.status === 404) {
        setError('City not found. It may have been deleted already.');
        setCities(cities.filter((city) => city._id !== id));
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete city');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVehicleDelete = async (id) => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vehicles/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        setSuccess('Bike deleted successfully');
        setVehicles(vehicles.filter((vehicle) => vehicle._id !== id));
      } else if (response.status === 404) {
        setError('Vehicle not found. It may have been deleted already.');
        setVehicles(vehicles.filter((vehicle) => vehicle._id !== id));
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete bike');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderCancel = async (id) => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        toast.success('Order canceled successfully', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        const [ordersResponse, vehiclesResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/orders`, { credentials: 'include' }),
          fetch(`${process.env.REACT_APP_API_URL}/api/vehicles`, { credentials: 'include' }),
        ]);
        if (ordersResponse.ok) {
          setOrders(await ordersResponse.json());
        }
        if (vehiclesResponse.ok) {
          setVehicles(await vehiclesResponse.json());
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to cancel order', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      toast.error('Network error. Please check your connection.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setBlockDatesVehicle(null);
    setBlockDatesForm({ dateFrom: '', timeFrom: '', dateTo: '', timeTo: '' });
  };

  const startEditingCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, image: null });
  };

  const startEditingCity = (city) => {
    setEditingCity(city);
    setCityForm({ state: city.state, city: city.city });
  };

  const startEditingVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      name: vehicle.name,
      type: vehicle.type,
      city: vehicle.city,
      prices: vehicle.prices,
      description: vehicle.description,
      image: null,
      imageUrl: vehicle.imageUrl,
      status: vehicle.status,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const cancelVehicleEdit = () => {
    setEditingVehicle(null);
    setVehicleForm({
      name: '',
      type: '',
      city: '',
      prices: { hourly: '', daily: '0', weekly: '', monthly: '' },
      description: '',
      image: null,
      imageUrl: '',
      status: 'Available',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const startBlockingDates = (vehicle) => {
    setBlockDatesVehicle(vehicle);
  };

  const handleBlockDatesSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/vehicles/${blockDatesVehicle._id}/unavailable-dates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(blockDatesForm),
          credentials: 'include',
        }
      );

      if (response.ok) {
        toast.success('Unavailable dates added successfully', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/vehicles`, {
          credentials: 'include',
        });
        if (refreshResponse.ok) {
          setVehicles(await refreshResponse.json());
        }
        closeModal();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to add unavailable dates', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      toast.error('Network error. Please check your connection.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user && error) {
    return <div className="admin-access-denied">{error}</div>;
  }

  if (!user) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-container">
      {isLoading && (
        <div className="admin-loader-overlay">
          <div className="admin-loader"></div>
        </div>
      )}
      <section className="admin-banner-section">
        <div className="admin-banner-overlay">
          <div className="admin-banner-content">
            <h1 className="admin-banner-title admin-visible">Admin Dashboard</h1>
            <p className="admin-banner-subtitle admin-visible">
              Manage categories, cities, bikes, and orders for Beaver Tours
            </p>
          </div>
        </div>
      </section>

      <div className="admin-tabs">
        <div className="admin-tab-list">
          <button
            className={`admin-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => handleTabChange('categories')}
            disabled={isLoading}
          >
            Categories
          </button>
          <button
            className={`admin-tab ${activeTab === 'cities' ? 'active' : ''}`}
            onClick={() => handleTabChange('cities')}
            disabled={isLoading}
          >
            Cities
          </button>
          <button
            className={`admin-tab ${activeTab === 'vehicles' ? 'active' : ''}`}
            onClick={() => handleTabChange('vehicles')}
            disabled={isLoading}
          >
            Vehicles
          </button>
          <button
            className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => handleTabChange('orders')}
            disabled={isLoading}
          >
            Orders
          </button>
        </div>
      </div>

      <div className="admin-tab-content">
        {activeTab === 'categories' && (
          <section className="admin-section">
            <div className="admin-wrapper">
              <div className="admin-section-header admin-fade-in admin-visible">
                <h2>Manage Categories</h2>
                <div className="admin-section-divider"></div>
              </div>

              <form className="admin-form admin-slide-up admin-visible" onSubmit={handleCategorySubmit}>
                <div className="admin-form-group">
                  <label htmlFor="category-name">Category Name</label>
                  <input
                    type="text"
                    id="category-name"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="Enter category name"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="category-image">Category Image (optional)</label>
                  <input
                    type="file"
                    id="category-image"
                    accept="image/*"
                    onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.files[0] })}
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-actions">
                  <button type="submit" className="admin-button admin-primary" disabled={isLoading}>
                    {editingCategory ? 'Update Category' : 'Add Category'}
                  </button>
                  {editingCategory && (
                    <button
                      type="button"
                      className="admin-button admin-secondary"
                      onClick={() => {
                        setEditingCategory(null);
                        setCategoryForm({ name: '', image: null });
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {error && <div className="admin-message admin-error">{error}</div>}
              {success && <div className="admin-message admin-success">{success}</div>}

              <div className="admin-table-container admin-slide-up admin-visible">
                {categories.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((category) => (
                        <tr key={category._id}>
                          <td className="admin-table-image-cell">
                            {category.imageUrl ? (
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                className="admin-table-image"
                              />
                            ) : (
                              'No Image'
                            )}
                          </td>
                          <td>{category.name}</td>
                          <td>
                            <div className="admin-table-actions">
                              <button
                                className="admin-button admin-primary admin-sm"
                                onClick={() => startEditingCategory(category)}
                                disabled={isLoading}
                              >
                                Edit
                              </button>
                              <button
                                className="admin-button admin-danger admin-sm"
                                onClick={() => handleCategoryDelete(category._id)}
                                disabled={isLoading}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No categories found. Add your first category above.</p>
                )}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'cities' && (
          <section className="admin-section">
            <div className="admin-wrapper">
              <div className="admin-section-header admin-fade-in admin-visible">
                <h2>Manage Cities</h2>
                <div className="admin-section-divider"></div>
              </div>

              <form className="admin-form admin-slide-up admin-visible" onSubmit={handleCitySubmit}>
                <div className="admin-form-group">
                  <label htmlFor="city-state">State</label>
                  <input
                    type="text"
                    id="city-state"
                    value={cityForm.state}
                    onChange={(e) => setCityForm({ ...cityForm, state: e.target.value })}
                    placeholder="Enter state name"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="city-name">City</label>
                  <input
                    type="text"
                    id="city-name"
                    value={cityForm.city}
                    onChange={(e) => setCityForm({ ...cityForm, city: e.target.value })}
                    placeholder="Enter city name"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-actions">
                  <button type="submit" className="admin-button admin-primary" disabled={isLoading}>
                    {editingCity ? 'Update City' : 'Add City'}
                  </button>
                  {editingCity && (
                    <button
                      type="button"
                      className="admin-button admin-secondary"
                      onClick={() => {
                        setEditingCity(null);
                        setCityForm({ state: '', city: '' });
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {error && <div className="admin-message admin-error">{error}</div>}
              {success && <div className="admin-message admin-success">{success}</div>}

              <div className="admin-table-container admin-slide-up admin-visible">
                {cities.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>State</th>
                        <th>City</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cities.map((city) => (
                        <tr key={city._id}>
                          <td>{city.state}</td>
                          <td>{city.city}</td>
                          <td>
                            <div className="admin-table-actions">
                              <button
                                className="admin-button admin-primary admin-sm"
                                onClick={() => startEditingCity(city)}
                                disabled={isLoading}
                              >
                                Edit
                              </button>
                              <button
                                className="admin-button admin-danger admin-sm"
                                onClick={() => handleCityDelete(city._id)}
                                disabled={isLoading}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No cities found. Add your first city above.</p>
                )}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'vehicles' && (
          <section className="admin-section">
            <div className="admin-wrapper">
              <div className="admin-section-header admin-fade-in admin-visible">
                <h2>Manage Bikes</h2>
                <div className="admin-section-divider"></div>
              </div>

              <form className="admin-form admin-slide-up admin-visible" onSubmit={handleVehicleSubmit}>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-name">Bike Name</label>
                  <input
                    type="text"
                    id="vehicle-name"
                    value={vehicleForm.name}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })}
                    placeholder="Enter bike name"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-type">Category</label>
                  <select
                    id="vehicle-type"
                    value={vehicleForm.type}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, type: e.target.value })}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-city">City</label>
                  <select
                    id="vehicle-city"
                    value={vehicleForm.city}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, city: e.target.value })}
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select City</option>
                    {cities.map((city) => (
                      <option key={city._id} value={city.city}>
                        {city.city}, {city.state}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-prices-hourly">Hourly Price (₹)</label>
                  <input
                    type="number"
                    id="vehicle-prices-hourly"
                    value={vehicleForm.prices.hourly}
                    onChange={(e) =>
                      setVehicleForm({
                        ...vehicleForm,
                        prices: { ...vehicleForm.prices, hourly: e.target.value },
                      })
                    }
                    placeholder="Hourly price in INR"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-prices-weekly">Weekly Price (₹)</label>
                  <input
                    type="number"
                    id="vehicle-prices-weekly"
                    value={vehicleForm.prices.weekly}
                    onChange={(e) =>
                      setVehicleForm({
                        ...vehicleForm,
                        prices: { ...vehicleForm.prices, weekly: e.target.value },
                      })
                    }
                    placeholder="Weekly price in INR"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-prices-monthly">Monthly Price (₹)</label>
                  <input
                    type="number"
                    id="vehicle-prices-monthly"
                    value={vehicleForm.prices.monthly}
                    onChange={(e) =>
                      setVehicleForm({
                        ...vehicleForm,
                        prices: { ...vehicleForm.prices, monthly: e.target.value },
                      })
                    }
                    placeholder="Monthly price in INR"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-description">Description</label>
                  <textarea
                    id="vehicle-description"
                    value={vehicleForm.description}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, description: e.target.value })}
                    placeholder="Enter description"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="admin-form-group">
                  <label htmlFor="vehicle-image">Bike Image</label>
                  {editingVehicle && vehicleForm.imageUrl && (
                    <div className="admin-current-image">
                      <p>Current Image:</p>
                      <img
                        src={vehicleForm.imageUrl}
                        alt="Current bike"
                        className="admin-vehicle-image-preview"
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    id="vehicle-image"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, image: e.target.files[0] })}
                    required={!editingVehicle}
                    disabled={isLoading}
                  />
                  {editingVehicle && <p>Upload a new image to replace the current one (optional).</p>}
                </div>
                <div className="admin-form-actions">
                  <button type="submit" className="admin-button admin-primary" disabled={isLoading}>
                    {editingVehicle ? 'Update Bike' : 'Add Bike'}
                  </button>
                  {editingVehicle && (
                    <button
                      type="button"
                      className="admin-button admin-secondary"
                      onClick={cancelVehicleEdit}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {error && <div className="admin-message admin-error">{error}</div>}
              {success && <div className="admin-message admin-success">{success}</div>}

              <div className="admin-list admin-slide-up admin-visible">
                {vehicles.length > 0 ? (
                  vehicles.map((vehicle) => (
                    <div key={vehicle._id} className="admin-list-item">
                      <img src={vehicle.imageUrl} alt={vehicle.name} />
                      <h3>{vehicle.name}</h3>
                      <p>Category: {vehicle.type}</p>
                      <p>City: {vehicle.city}</p>
                      <p>Hourly: ₹{vehicle.prices.hourly}</p>
                      <p>Weekly: ₹{vehicle.prices.weekly}</p>
                      <p>Monthly: ₹{vehicle.prices.monthly}</p>
                      <p>
                        <strong>Unavailable Dates:</strong>
                        {vehicle.unavailableDates && vehicle.unavailableDates.length > 0 ? (
                          <ul>
                            {vehicle.unavailableDates.map((range, index) => (
                              <li key={index}>
                                {new Date(range.from).toLocaleString('en-IN', {
                                  timeZone: 'Asia/Kolkata',
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })}{' '}
                                -{' '}
                                {new Date(range.to).toLocaleString('en-IN', {
                                  timeZone: 'Asia/Kolkata',
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          'None'
                        )}
                      </p>
                      <div className="admin-list-item-actions">
                        <button
                          className="admin-button admin-primary"
                          onClick={() => startEditingVehicle(vehicle)}
                          disabled={isLoading}
                        >
                          Edit
                        </button>
                        <button
                          className="admin-button admin-danger"
                          onClick={() => handleVehicleDelete(vehicle._id)}
                          disabled={isLoading}
                        >
                          Delete
                        </button>
                        <button
                          className="admin-button admin-secondary"
                          onClick={() => startBlockingDates(vehicle)}
                          disabled={isLoading}
                        >
                          Block Dates
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No bikes found. Add your first bike above.</p>
                )}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'orders' && (
          <section className="admin-section">
            <div className="admin-wrapper">
              <div className="admin-section-header admin-fade-in admin-visible">
                <h2>Manage Orders</h2>
                <div className="admin-section-divider"></div>
              </div>

              {error && <div className="admin-message admin-error">{error}</div>}
              {success && <div className="admin-message admin-success">{success}</div>}

              <div className="admin-table-container admin-slide-up admin-visible">
                {orders.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Vehicle</th>
                        <th>Customer</th>
                        <th>Rental Period</th>
                        <th>Total Price (₹)</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => {
                        // Convert order.dateTo to Date object if it's a string
                        const dateTo = typeof order.dateTo === 'string' ? new Date(order.dateTo) : order.dateTo;
                        // Validate dateTo and timeTo
                        const isValidDate = dateTo instanceof Date && !isNaN(dateTo) && order.timeTo;
                        // Parse end date-time (IST)
                        const endDateTime = isValidDate
                          ? new Date(`${dateTo.toISOString().split('T')[0]}T${order.timeTo}:00+05:30`)
                          : null;
                        const now = new Date();
                        const isFuture = endDateTime && endDateTime > now;

                        return (
                          <tr
                            key={order._id}
                            onClick={() => handleOrderClick(order)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{order._id}</td>
                            <td>{order.vehicleId?.name || 'Unknown'}</td>
                            <td>{order.name}</td>
                            <td>
                              {new Date(order.dateFrom).toLocaleDateString('en-IN', {
                                timeZone: 'Asia/Kolkata',
                              })}{' '}
                              {order.timeFrom} -{' '}
                              {isValidDate
                                ? `${dateTo.toLocaleDateString('en-IN', {
                                    timeZone: 'Asia/Kolkata',
                                  })} ${order.timeTo}`
                                : 'Invalid Date'}
                            </td>
                            <td>{order.totalPrice}</td>
                            <td>{order.status.charAt(0).toUpperCase() + order.status.slice(1)}</td>
                            <td>
                              <div className="admin-table-actions">
                                {order.status === 'active' && isFuture && (
                                  <button
                                    className="admin-button admin-danger admin-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOrderCancel(order._id);
                                    }}
                                    disabled={isLoading}
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p>No orders found.</p>
                )}
              </div>
            </div>
          </section>
        )}
      </div>

      {selectedOrder && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h2>Order Details</h2>
            <div className="admin-modal-content">
              <p>
                <strong>Order ID:</strong> {selectedOrder._id}
              </p>
              <p>
                <strong>Vehicle:</strong> {selectedOrder.vehicleId?.name || 'Unknown'}
              </p>
              <p>
                <strong>Customer Name:</strong> {selectedOrder.name}
              </p>
              <p>
                <strong>Email:</strong> {selectedOrder.email || 'N/A'}
              </p>
              <p>
                <strong>Contact:</strong> {selectedOrder.contact}
              </p>
              <p>
                <strong>Address:</strong> {selectedOrder.address}
              </p>
              <p>
                <strong>Rental Period:</strong>{' '}
                {new Date(selectedOrder.dateFrom).toLocaleDateString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                })}{' '}
                {selectedOrder.timeFrom} to{' '}
                {new Date(selectedOrder.dateTo).toLocaleDateString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                })}{' '}
                {selectedOrder.timeTo}
              </p>
              <p>
                <strong>Pricing Type:</strong> {selectedOrder.pricingType}
              </p>
              <p>
                <strong>Total Price (with 5% GST):</strong> ₹{selectedOrder.totalPrice}
              </p>
              {/* <p>
                <strong>Payment Status:</strong>{' '}
                {selectedOrder.paymentStatus + selectedOrder.paymentStatus}
              </p> */}
              <p>
                <strong>Agreed to Terms:</strong> {selectedOrder.agreeTerms ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Has License:</strong> {selectedOrder.hasLicense ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Status:</strong>{' '}
                {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
              </p>
            </div>
            <div className="admin-modal-actions">
              <button className="admin-button admin-secondary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {blockDatesVehicle && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h2>Block Dates for {blockDatesVehicle.name}</h2>
            <form className="admin-form" onSubmit={handleBlockDatesSubmit}>
              <div className="admin-form-group">
                <label htmlFor="block-date-from">Start Date</label>
                <input
                  type="date"
                  id="block-date-from"
                  value={blockDatesForm.dateFrom}
                  onChange={(e) =>
                    setBlockDatesForm({ ...blockDatesForm, dateFrom: e.target.value })
                  }
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="block-time-from">Start Time</label>
                <input
                  type="time"
                  id="block-time-from"
                  value={blockDatesForm.timeFrom}
                  onChange={(e) =>
                    setBlockDatesForm({ ...blockDatesForm, timeFrom: e.target.value })
                  }
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="block-date-to">End Date</label>
                <input
                  type="date"
                  id="block-date-to"
                  value={blockDatesForm.dateTo}
                  onChange={(e) =>
                    setBlockDatesForm({ ...blockDatesForm, dateTo: e.target.value })
                  }
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="block-time-to">End Time</label>
                <input
                  type="time"
                  id="block-time-to"
                  value={blockDatesForm.timeTo}
                  onChange={(e) =>
                    setBlockDatesForm({ ...blockDatesForm, timeTo: e.target.value })
                  }
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="admin-form-actions">
                <button type="submit" className="admin-button admin-primary" disabled={isLoading}>
                  Block Dates
                </button>
                <button
                  type="button"
                  className="admin-button admin-secondary"
                  onClick={closeModal}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;